import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { bffInstance } from "@shared/services";
import { BACKOFFICE_QUERIES } from "./queries";

import type {
  IPOSCompaniesResponse,
  IPOSSellersResponse,
  POSBatchProcessResultResponse,
} from "../types/company";

const BACKOFFICE_TERMINALS = "/backoffice/v1";
const BARTE_POS_TERMINALS = "/barte/v1/pos";

const api = bffInstance({
  enableXTokenApi: false,
  enableBearerToken: true,
});

export const useGetPOSCompanies = ({
  page = 0,
  size = 10,
  companyId,
  enabled = true,
}: {
  page?: number;
  size?: number;
  companyId?: number;
  enabled?: boolean;
} = {}) => {
  const { t } = useTranslation();

  const getPOSCompanies = async (): Promise<{
    success: boolean;
    message: string;
    data?: IPOSCompaniesResponse;
  }> => {
    try {
      const { data } = await api.get(`${BACKOFFICE_TERMINALS}/companies/pos`, {
        params: {
          page,
          size,
          companyId,
        },
      });

      return {
        success: true,
        message: t("backoffice.companiesServiceGetPOSCompaniesSuccess"),
        data,
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.message ||
          error?.response?.message ||
          t("backoffice.companiesServiceGetPOSCompaniesError"),
      };
    }
  };

  return useQuery({
    queryKey: [BACKOFFICE_QUERIES.COMPANIES, "pos", page, size, companyId],
    queryFn: getPOSCompanies,
    enabled,
  });
};

export const useGetPOSSellersByCompany = ({
  companyId,
  barteSellerId,
  sellerName,
  page = 0,
  size = 50,
}: {
  companyId?: number;
  barteSellerId?: number;
  sellerName?: string;
  page?: number;
  size?: number;
}) => {
  const { t } = useTranslation();

  const getPOSSellersByCompany = async (): Promise<{
    success: boolean;
    message: string;
    data?: IPOSSellersResponse;
  }> => {
    try {
      const { data } = await api.get(
        `${BACKOFFICE_TERMINALS}/companies/${companyId}/sellers/pos`,
        {
          params: {
            page,
            size,
            sellerId: barteSellerId,
            sellerName,
          },
        },
      );

      return {
        success: true,
        message: t("backoffice.companiesServiceGetPOSSellersSuccess"),
        data,
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.message ||
          error?.response?.message ||
          t("backoffice.companiesServiceGetPOSSellersError"),
      };
    }
  };

  return useQuery({
    queryKey: [
      BACKOFFICE_QUERIES.COMPANIES,
      "sellers-pos",
      companyId,
      barteSellerId,
      sellerName,
      page,
      size,
    ],
    queryFn: getPOSSellersByCompany,
    enabled: !!companyId,
  });
};

export const useCreatePOSSeller = () => {
  const { t } = useTranslation();

  const createPOSSeller = async ({
    posCompanyId,
    barteSellerId,
    skinId,
  }: {
    posCompanyId: number;
    barteSellerId: number;
    skinId?: number | null;
  }): Promise<{
    success: boolean;
    message: string;
    data?: {
      id: number;
      sellerId: number;
      sellerName: string;
      externalId: string;
      partner: string;
    };
    rawData?: any;
  }> => {
    try {
      const { data } = await api.post(
        `${BARTE_POS_TERMINALS}/companies/${posCompanyId}/barte-sellers/${barteSellerId}/sellers`,
        {
          skinId: skinId ?? null,
        },
      );

      return {
        success: true,
        message: t("backoffice.companiesServiceCreatePOSSellerSuccess"),
        data,
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.errors?.[0]?.description ||
          error?.response?.data?.message ||
          error?.response?.message ||
          t("backoffice.companiesServiceCreatePOSSellerError"),
        rawData: error?.response?.data,
      };
    }
  };

  return useMutation({
    mutationKey: [BACKOFFICE_QUERIES.COMPANIES, "create-pos-seller"],
    mutationFn: ({
      posCompanyId,
      barteSellerId,
      skinId,
    }: {
      posCompanyId: number;
      barteSellerId: number;
      skinId?: number | null;
    }) => createPOSSeller({ posCompanyId, barteSellerId, skinId }),
  });
};

export const useProcessTerminalCsv = () => {
  const { t } = useTranslation();

  const processTerminalCsv = async (
    file: File,
  ): Promise<{
    success: boolean;
    message: string;
    data?: POSBatchProcessResultResponse;
  }> => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await api.post(
        `${BACKOFFICE_TERMINALS}/terminals/csv`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      return {
        success: true,
        message: t("backoffice.companiesServiceProcessTerminalCsvSuccess"),
        data,
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.message ||
          error?.response?.message ||
          t("backoffice.companiesServiceProcessTerminalCsvError"),
      };
    }
  };

  return useMutation({
    mutationKey: [BACKOFFICE_QUERIES.COMPANIES, "process-terminal-csv"],
    mutationFn: processTerminalCsv,
  });
};

export const useCreatePOSCompany = () => {
  const { t } = useTranslation();

  const createPOSCompany = async (
    barteCompanyId: number,
  ): Promise<{
    success: boolean;
    message: string;
    data?: {
      id: number;
      name: string;
      posCompanyId: number;
    };
    rawData?: any;
  }> => {
    try {
      const { data } = await api.post(
        `${BARTE_POS_TERMINALS}/companies/barte-companies/${barteCompanyId}`,
      );

      return {
        success: true,
        message: t("backoffice.companiesServiceCreatePOSCompanySuccess"),
        data,
      };
    } catch (error: any) {
      return {
        success: false,
        message:
          error?.response?.data?.errors?.[0]?.description ||
          error?.response?.data?.message ||
          error?.response?.message ||
          t("backoffice.companiesServiceCreatePOSCompanyError"),
        rawData: error?.response?.data,
      };
    }
  };

  return useMutation({
    mutationKey: [BACKOFFICE_QUERIES.COMPANIES, "create-pos-company"],
    mutationFn: createPOSCompany,
  });
};
